<!-- eslint-disable max-len -->
<script lang="ts" setup>
import { ref, onBeforeMount, onMounted } from 'vue';
import { requirejs } from '../modules/require';
import { useRouter } from 'vue-router';
import { useLabStore } from '~/stores/lab';
import $fetch from '~/modules/fetch';

import MPUIOrderList from '@ds/marketplace-ui/orderList';
import { Cart, CartsV2CartState } from '@ds/marketplace-ui/orderList';
import { MarketplaceApi } from '@ds/marketplace-ui/core';
// import '@ds/marketplace-ui/marketplace-ui.css';

import OrderListCustomPartView from '~/components/order/orderListCustomPartView.vue';

// PROPS
interface Props {
  isSeller?: boolean;
  showDraftState?: boolean;
  defaultState?: string;
  numberCartsPerPage?: number;
  isOperator?: boolean;
  onReOrder?: any;
  onOpenCart?: any;
  onContinueQuote?: any;
  onForward?: any;
}

const props = withDefaults(defineProps<Props>(), {
  isSeller: false,
  showDraftState: false,
  defaultState: CartsV2CartState.ALL,
});

// STORES
const router = useRouter();
const lab = useLabStore();

// VARIABLES
const loading = ref(true);
const tou = ref<any>(null);
let labId: string;

// METHODS
const handleOnContinueQuote = (cart: Cart) => {
  if (props.onContinueQuote) {
    props.onContinueQuote(cart);
  } else {
    continueQuote(cart);
  }
};

const handleOnForward = (cart: Cart) => {
  if (props.onForward) {
    props.onForward(cart);
  } else {
    router.push({ name: 'QuoteEdition', query: { forward: cart.id } });
  }
};

const handleOnOpenCart = (cart: Cart) => {
  if (props.onOpenCart) {
    props.onOpenCart(cart);
  } else {
    if (cart.GlobalState.toLowerCase() !== CartsV2CartState.DRAFT) {
      router.push({ name: 'Bidding', params: { orderId: cart.id } });
    } else {
      continueQuote(cart);
    }
  }
};

const handleOnReOrder = (cart: Cart) => {
  if (props.onReOrder) {
    props.onReOrder(cart);
  } else {
    router.push({ name: 'QuoteEdition', query: { from: cart.id } });
  }
};

const loadTermsOfUse = async () => {
  const [MP3DPConnector, TCDocumentFactory, TermsOfUseOrderList] = await requirejs<
    [MP3DPConnector, any, any]
  >([
    'DS/MP3DPModels/MP3DPConnector',
    'DS/MPFTcDocumentModel/TCDocumentFactory',
    'DS/MPFTcContractComponent/TermsOfUseOrderList',
  ]);

  MP3DPConnector.getConnector((iConnector: any) => {
    const docFacto = new TCDocumentFactory(iConnector);
    new TermsOfUseOrderList({
      tcDocumentFactory: docFacto,
    })
      .render()
      .inject(tou.value);
  });
};

const continueQuote = async (cart: any) => {
  localStorage.setItem('vue-cart-id', cart.id);
  router.push({ name: 'QuoteEdition' });
};

onMounted(async () => {
  const mpdatamgtUrl = (await $fetch.getServiceURL('mpdatamgt')) + '/resources';
  const tenant = await $fetch.getTenant();

  MarketplaceApi.configure({
    baseUrl: mpdatamgtUrl,
    platformId: tenant,
    service: '3dprinting',
    language: 'en',
  });

  // We are on the My Request page
  if (props.showDraftState) {
    await loadTermsOfUse();
  }

  loading.value = false;
});

onBeforeMount(async () => {
  if (props.isSeller) {
    labId = lab.lab?.id || String(router.currentRoute.value.params?.id);
  }

  const [MP3DPNotificationCenter] = await requirejs<[NotificationCenter]>([
    'DS/MP3DPServices/MP3DPNotificationCenter',
  ]);
  const notificationCenter = MP3DPNotificationCenter.getInterWidgetNotificationCenter();

  notificationCenter.subscribe('changePage', (data: any) => {
    const { intlabid } = router.currentRoute.value.query;
    const params = [];

    if (data && data.name === 'order' && data.attributes && data.attributes.id) {
      if (intlabid) {
        params.push({ name: 'intlabid', param: intlabid });
      }

      router.push({ name: 'Bidding', params: { orderId: data.attributes.id } });
    }
  });
});
</script>

<template>
  <div class="order-list-view">
    <div v-if="((labId && props.isSeller) || !props.isSeller) && !loading" class="orders-list">
      <MPUIOrderList
        service="3dprinting"
        :number-carts-per-page="5"
        :is-operator="false"
        :is-seller="props.isSeller"
        :show-draft-state="props.showDraftState"
        :default-state="props.defaultState"
        :shop-id="labId"
        @forward="handleOnForward"
        @re-order="handleOnReOrder"
        @open-cart="handleOnOpenCart"
        @continue-quote="handleOnContinueQuote"
      >
        <template #service-view="serviceViewProps">
          <OrderListCustomPartView :cart="serviceViewProps.cart" />
        </template>
      </MPUIOrderList>
    </div>
    <div v-if="props.showDraftState" ref="tou" class="order-list-terms-of-use mb-20px" />
  </div>
</template>

<style scoped lang="scss">
@use '/assets/scss/variables' as variables;

@media (min-width: variables.$screen-sm-max) {
  .order-list-view {
    min-width: variables.$screen-sm-max;
  }
}

.orders-list {
  min-height: 400px;
}

.order-list-view {
  width: 100%;
}

.order-list-view:deep(.mpui-order-list) {
  .modal-overlay {
    background-color: transparent;
  }

  .mpf-order-card {
    background-color: white;
  }

  .orders-list h1 {
    display: none;
  }

  @media (min-width: 1599px) {
    .mpui-order-list {
      padding: 48px 0px;
    }
  }
}
</style>
